<template>
  <div
    id="panelManager"
    :class="{ 'drawer-container-legacy': !isNewPhotoEditor, 'drawer-container': isNewPhotoEditor }"
  >
    <div v-if="editorStore.openedPanel === 'fx'">
      <FxDrawer
        :foreground-type="editorStore.selectedImage.meta.foregroundType"
        :selected-image-state="currentPersistentStore"
        :getDataURL="props.getDataURL"
        :stage="props.stage"
        :stageHd="props.stageHd"
        :refreshAllHdLayers="props.refreshAllHdLayers"
        :persistentStore="props.currentPersistentStore"
        @close-panel="editorStore.closePanel"
      />
    </div>
    <div v-if="editorStore.openedPanel === 'addBackground'">
      <DrawerAddBackground
        v-if="isSmallTablet || !isNewPhotoEditor"
        @close-panel="() => props.togglePanel('addBackground')"
        @reset="props.restoreOriginalResult"
        :foreground-type="editorStore.selectedImage.meta.foregroundType"
        :selected-image-state="props.currentPersistentStore"
      />
      <FullHeightDrawerAddBackground
        v-else-if="isNewPhotoEditor"
        @close-panel="() => props.togglePanel('addBackground')"
        @reset="props.restoreOriginalResult"
        :foreground-type="editorStore.selectedImage.meta.foregroundType"
        :selected-image-state="props.currentPersistentStore"
      />
    </div>
    <div v-if="editorStore.openedPanel === 'eraseRestore'">
      <DrawerEraseRestore
        :editor="props.editor"
        :stage="props.stage"
        :stageHd="props.stageHd"
        :scale="props.scale"
        :getDataURL="props.getDataURL"
        :refreshAllHdLayers="props.refreshAllHdLayers"
        :persistentStore="props.currentPersistentStore"
        @close-panel="() => props.togglePanel('eraseRestore')"
        @reset="restoreOriginalResult"
      />
    </div>
    <div v-if="editorStore.openedPanel === 'adjust'">
      <DrawerAdjust
        :getDataURL="props.getDataURL"
        :editor="props.editor"
        :stage="props.stage"
        :stageHd="props.stageHd"
        :persistentStore="props.currentPersistentStore"
        :refreshAllHdLayers="props.refreshAllHdLayers"
        @close-panel="() => props.togglePanel('adjust')"
      />
    </div>
    <div v-if="editorStore.openedPanel === 'design'">
      <DrawerDesign
        :getDataURL="props.getDataURL"
        :editor="props.editor"
        :stage="props.stage"
        :stageHd="props.stageHd"
        :persistentStore="props.currentPersistentStore"
        :refreshAllHdLayers="props.refreshAllHdLayers"
        @close-panel="() => props.togglePanel('design')"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { Panel, useEditorStore } from "@/stores/editor_store";
import DrawerAddBackground from "@/components/upload/drawer_add_background.vue";
import FullHeightDrawerAddBackground from "@/components/upload/panels/drawer_background.vue";
import DrawerEraseRestore from "@/components/upload/drawer_erase_restore.vue";
import DrawerAdjust from "@/components/upload/drawer_adjust.vue";
import DrawerDesign from "@/components/upload/drawer_design.vue";
import FxDrawer from "@/components/upload/fx_drawer.vue";
import { Stage } from "konva/lib/Stage";
import { useMonitorSize } from "@/composables/monitor_size";
import { onMounted, onUnmounted, nextTick } from "vue";
import { PersistentStore } from "@/stores/persistent_store";
import { Image } from "@/modules/internal_api/image";
import { usePhotoEditor } from "@/composables/photo_editor";

interface DrawerManagerProps {
  currentPersistentStore: PersistentStore;
  editor: HTMLElement;
  stage: Stage;
  stageHd: Stage;
  scale: number;
  restoreOriginalResult: () => void;
  togglePanel: (id: Panel) => void;
  getDataURL: Function;
  refreshAllHdLayers: (image: Image) => Promise<void>;
}

const props = defineProps<DrawerManagerProps>();
const editorStore = useEditorStore();
const { isSmallTablet } = useMonitorSize();

const { isNewEditorEnabled: isNewPhotoEditor } = usePhotoEditor();

const closeDesktopOnlyPanels = () => {
  if (isSmallTablet.value) {
    if (editorStore.openedPanel === "adjust") {
      props.togglePanel("adjust");
    }
    if (editorStore.openedPanel === "design") {
      props.togglePanel("design");
    }
  }
};

const handleResize = () => {
  nextTick(() => closeDesktopOnlyPanels());
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>
<style scoped>
.drawer-container {
  @media (min-width: 1024px) {
    max-width: 18rem;
  }
}

.outer-wrapper {
  @media (max-width: 1023px) {
    flex-direction: column !important;
  }
}

.drawer-container-legacy {
  @media (min-width: 1024px) {
    max-width: 24.25rem;
  }
}
</style>
<style>
#panelManager .scrollable-drawer,
.scrollable-drawer {
  /**
    * The max height is calculated as follows:
    * - navbar height: 72px
    * - toolbar height: 72px
    * - image navigation height: 90px
    * - editor navigation spacing: 16 * 2
    */
  max-height: calc(100vh - 266px);
  overflow-y: auto !important;
}

.drawer-container .relative.flex-grow {
  /* this is the only selector I could come up with to add a border to the drawer prism element
  todo: should be using border-surface-border, should be made more modular through prism probably */
  border-width: 1px;
  border-color: #001b2d0d;
}
</style>
